/**
 * De Veilige Haven (medewerkersportaal onderdeel 60, migratie 193):
 * Chat-Matrix, Directe Hulplijn en Mijn Koffer.
 */
import api from './api';

const d = <T,>(r: { data: { data: T } }) => r.data.data;

// ─── Chat-Matrix ─────────────────────────────────────────────────────────────

export interface ChatKanaal {
  id: string;
  casus_nummer: number;
  status: 'ENABLED' | 'DISABLED';
  gesloten: boolean;
  ongelezen: number;
  laatste_bericht_op: string | null;
  /** Alleen aan de medewerkerskant */
  professional_naam?: string;
  discipline_label?: string;
}

export interface ChatBericht {
  id: string;
  van: 'ik' | 'ander';
  tekst: string | null;
  created_at: string;
}

export const CHAT_KANALEN_KEY = ['veilige-chat', 'kanalen'] as const;
export const CHAT_ONGELEZEN_KEY = ['veilige-chat', 'ongelezen'] as const;

export const chatApi = {
  kanalen: () => api.get<{ data: ChatKanaal[] }>('/v1/chat/kanalen').then(d),
  ongelezen: () => api.get<{ data: { aantal: number } }>('/v1/chat/ongelezen').then(d),
  berichten: (kanaalId: string) => api.get<{ data: { kanaal: ChatKanaal; berichten: ChatBericht[] } }>(`/v1/chat/kanalen/${kanaalId}/berichten`).then(d),
  verstuur: (kanaalId: string, tekst: string) => api.post<{ data: ChatBericht }>(`/v1/chat/kanalen/${kanaalId}/berichten`, { tekst }).then(d),
};

// ─── Directe Hulplijn ────────────────────────────────────────────────────────

export interface HulplijnDiscipline {
  professional_type: string;
  label: string;
  aantal: number;
}

export interface HulplijnVerzoek {
  id: string;
  professional_type: string;
  discipline_label: string;
  status: 'OPEN' | 'CLAIMED' | 'AFGEROND' | 'GEANNULEERD';
  created_at: string;
  claimed_at: string | null;
  professional_naam: string | null;
}

export interface HulplijnWachtrij {
  open: { id: string; discipline_label: string; bedrijfsnaam: string | null; wacht_sinds: string }[];
  geclaimd: { id: string; discipline_label: string; claimed_at: string; terugbelnummer: string | null; kanaal_id: string | null; casus_nummer: number | null }[];
}

export const HULPLIJN_KEY = ['medewerker-hulplijn'] as const;
export const HULPLIJN_WACHTRIJ_KEY = ['professional-hulplijn'] as const;

export const hulplijnApi = {
  status: () => api.get<{ data: { disciplines: HulplijnDiscipline[]; verzoeken: HulplijnVerzoek[] } }>('/v1/medewerker/hulplijn').then(d),
  verzoek: (professional_type: string, terugbelnummer: string) =>
    api.post<{ data: { id: string; discipline_label: string } }>('/v1/medewerker/hulplijn', { professional_type, terugbelnummer }).then(d),
  annuleer: (id: string) => api.post(`/v1/medewerker/hulplijn/${id}/annuleren`),
  wachtrij: () => api.get<{ data: HulplijnWachtrij }>('/v1/professional/hulplijn').then(d),
  claim: (id: string) =>
    api.post<{ data: { id: string; terugbelnummer: string | null; kanaal_id: string; casus_nummer: number } }>(`/v1/professional/hulplijn/${id}/claim`).then(d),
  afronden: (id: string) => api.post(`/v1/professional/hulplijn/${id}/afronden`),
};

// ─── Mijn Koffer ─────────────────────────────────────────────────────────────

/** Kofferticket na pincode: alleen in het geheugen, dus na herladen opnieuw de pincode. */
let kofferTicket: string | null = null;
export const zetKofferTicket = (t: string | null) => {
  kofferTicket = t;
};
export const heeftKofferTicket = () => kofferTicket !== null;
const kofferHeaders = () => (kofferTicket ? { 'x-koffer-ticket': kofferTicket } : {});

/** Tucht- of geschilleninstantie van een professional (onderdeel 59B) */
export interface TuchtInstantie {
  code: 'LVV' | 'WKKGZ' | 'COACH' | 'BIG' | 'NIP' | 'STM' | 'B2B';
  naam: string;
  url: string | null;
}

export interface KofferInhoud {
  trajecten: {
    afspraken: { id: string; start_tijd: string; status: string; professional_naam: string; discipline_label: string }[];
    actieplannen: { id: string; datum: string; professional: string }[];
  };
  academie: {
    wallet: import('./medewerkerApi').Wallet;
    trainingen: { id: string; titel: string; categorie_label: string; wallet_deel: number; behaald: boolean; sessie_start: string | null; sessie_eind: string | null }[];
  };
  escalatie: {
    professionals: { professional_id: string; naam: string; instanties: TuchtInstantie[] }[];
    klachten: {
      id: string;
      referentie: string;
      status: 'ingediend' | 'doorgestuurd' | 'afgehandeld';
      ingediend_op: string;
      professional: string;
      instanties: TuchtInstantie[];
      doorgestuurd_naar: TuchtInstantie | null;
    }[];
  };
}

export const KOFFER_STATUS_KEY = ['medewerker-koffer', 'status'] as const;
export const KOFFER_KEY = ['medewerker-koffer', 'inhoud'] as const;

export const kofferApi = {
  status: () => api.get<{ data: { pin_ingesteld: boolean; geblokkeerd_tot: string | null } }>('/v1/medewerker/koffer/status').then(d),
  ontgrendel: (pin: string) => api.post<{ data: { ticket: string } }>('/v1/medewerker/koffer/ontgrendel', { pin }).then(d),
  stelPinIn: (pin: string, huidige_pin?: string) => api.put<{ data: { ticket: string } }>('/v1/medewerker/koffer/pin', { pin, huidige_pin }).then(d),
  verwijderPin: (huidige_pin: string) => api.delete('/v1/medewerker/koffer/pin', { data: { huidige_pin } }),
  inhoud: () => api.get<{ data: KofferInhoud }>('/v1/medewerker/koffer', { headers: kofferHeaders() }).then(d),
  actieplanPdf: (id: string) => api.get<Blob>(`/v1/medewerker/koffer/actieplannen/${id}/pdf`, { headers: kofferHeaders(), responseType: 'blob' }).then((r) => r.data),
  wkkgzKlacht: (invoer: { professional_id: string; omschrijving: string; gewenste_uitkomst: string | null }) =>
    api
      .post<{ data: { id: string; referentie: string } }>('/v1/medewerker/koffer/wkkgz-klacht', { ...invoer, bevestigd: true }, { headers: kofferHeaders() })
      .then(d),
};
